import React, { useContext } from "react";
import { MapPin, Star } from "lucide-react";

import { formatRatingAverage } from "helpers/formatRatingAverage";
import { cn } from "lib/utils";
import { IntlContext } from "context";
import type { ServiceInterface } from "types/service";
import Image from "components/File/Image";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { VerifiedProfileBadge } from "./VerifiedProfileBadge";

type Provider = ServiceInterface["provider"];

export interface ProviderIdentityProps {
    provider: Provider;
    size?: "sm" | "lg";
    showVerifiedBadge?: boolean;
    className?: string;
}

function providerInitials(provider: Provider): string {
    return `${provider.first_name?.charAt(0) ?? ""}${provider.last_name?.charAt(0) ?? ""}`;
}

function formatPostcode(postcode?: string): string {
    return postcode ? ` (${postcode.substring(0, 5)})` : "";
}

export const ProviderIdentity: React.FC<ProviderIdentityProps> = ({
    provider,
    size = "sm",
    showVerifiedBadge = true,
    className,
}) => {
    const intl = useContext(IntlContext);
    const hasRating = !!provider.rating?.average;
    const isLarge = size === "lg";

    return (
        <div className={cn("flex items-start gap-3", className)}>
            <Avatar shape={isLarge ? "tileLg" : "tileSm"}>
                {provider.picture_id && (
                    <Image id={provider.picture_id} className="aspect-square h-full w-full object-cover" />
                )}
                <AvatarFallback shape={isLarge ? "tileLg" : "tileSm"} className={isLarge ? "text-lg font-bold" : "text-sm font-semibold"}>
                    {providerInitials(provider)}
                </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                    {isLarge ? (
                        <h1 className="text-xl font-bold leading-none text-foreground">
                            {provider.first_name} {provider.last_name?.charAt(0)}.
                        </h1>
                    ) : (
                        <span className="text-sm font-semibold leading-none">
                            {provider.first_name} {provider.last_name?.charAt(0)}.
                        </span>
                    )}
                    {showVerifiedBadge && provider.dataleon_status === "approved" && (
                        <VerifiedProfileBadge size={isLarge ? "lg" : "sm"} />
                    )}
                </div>

                {provider.city && (
                    <div
                        className={cn(
                            "flex items-center gap-1 text-muted-foreground",
                            isLarge ? "mt-2 text-sm" : "mt-1.5 text-xs",
                        )}
                    >
                        <MapPin className={cn("shrink-0", isLarge ? "h-3.5 w-3.5" : "h-3 w-3")} />
                        <span className={cn(!isLarge && "truncate")}>
                            {provider.city}
                            {formatPostcode(provider.postcode)}
                        </span>
                    </div>
                )}

                <div className={cn("flex items-center gap-1.5", isLarge ? "mt-2 text-sm" : "mt-1 text-xs")}>
                    <Star className={`fill-amber-400 text-amber-400 ${isLarge ? "h-4 w-4" : "h-3 w-3"}`} />
                    {hasRating ? (
                        <>
                            <span className={`font-bold ${isLarge ? "" : "text-foreground"}`}>
                                {formatRatingAverage(provider.rating.average)}
                            </span>
                            <span className="text-muted-foreground">
                                {intl.formatMessage("provider.view.rating_reviews", {
                                    count: provider.rating.count,
                                })}
                            </span>
                        </>
                    ) : (
                        <span className="text-muted-foreground">{intl.formatMessage("provider.card.rating_new")}</span>
                    )}
                </div>
            </div>
        </div>
    );
};
